import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, Sparkles, Loader2, Paperclip, X, FileText, RefreshCw } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiErrorMessage } from "@/lib/api";

const LANGS = [
  { code: "es", label: "Español" },
  { code: "en", label: "English" },
  { code: "it", label: "Italiano" },
  { code: "fr", label: "Français" },
  { code: "pt", label: "Português" },
  { code: "de", label: "Deutsch" },
];

const fmtSize = (n) => {
  if (n == null) return "";
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

/** Selector de adjuntos: sube a /asistente-trip/attachments/upload (auth SOFI). */
function AttachmentPicker({ items, onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const uploaded = [];
    for (const f of files) {
      if (f.size > 15 * 1024 * 1024) { toast.error(`${f.name} supera el máximo de 15 MB`); continue; }
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/asistente-trip/attachments/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(data);
      } catch (err) {
        toast.error(apiErrorMessage(err, `No se pudo subir ${f.name}`));
      }
    }
    if (uploaded.length) onChange([...items, ...uploaded]);
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const remove = (fid) => onChange(items.filter((a) => a.file_id !== fid));

  return (
    <div className="space-y-1.5">
      <input ref={inputRef} type="file" multiple className="hidden" onChange={onFiles} data-testid="composer-att-input" />
      <div className="flex flex-wrap items-center gap-1.5">
        <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
          className="inline-flex items-center gap-1.5 border border-dashed border-border rounded-md px-2 py-1 text-[11px] text-muted-foreground hover:border-primary hover:text-primary transition-colors"
          data-testid="composer-att-button">
          {uploading ? <Loader2 size={11} className="animate-spin" /> : <Paperclip size={11} />}
          {uploading ? "Subiendo…" : "Adjuntar archivo"}
        </button>
        {items.map((a) => (
          <span key={a.file_id} className="inline-flex items-center gap-1.5 bg-primary/10 border border-primary/20 text-primary rounded-md px-2 py-1 text-[11px]"
            data-testid={`composer-att-item-${a.file_id}`}>
            <FileText size={11} />
            <span className="max-w-[180px] truncate">{a.filename}</span>
            <span className="opacity-60">{fmtSize(a.size)}</span>
            <button type="button" onClick={() => remove(a.file_id)} className="opacity-60 hover:text-destructive"
              data-testid={`composer-att-remove-${a.file_id}`}><X size={11} /></button>
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * Composer enriquecido integrado en SOFI.
 * props: { open, onClose(refresh), tripId, providerId, reservaIds, toEmail, providerName, multi, emailConnected, senderEmail }
 */
export default function TripEmailComposer({
  open, onClose, tripId, providerId, reservaIds = [], toEmail = "",
  providerName = "", multi = false, emailConnected = false, senderEmail = "",
  apiBase = "/asistente-trip", titleSingle = "Componer correo de reserva",
}) {
  const [templates, setTemplates] = useState([]);
  const [language, setLanguage] = useState("es");
  const [templateId, setTemplateId] = useState("auto");
  const [to, setTo] = useState(toEmail || "");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [rendering, setRendering] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get(`${apiBase}/templates`).then(({ data }) => setTemplates(data)).catch(() => {});
  }, [apiBase]);

  const doRender = async (overrides = {}) => {
    if (!providerId || reservaIds.length === 0) return;
    setRendering(true);
    try {
      const tid = overrides.template_id !== undefined ? overrides.template_id : (templateId === "auto" ? null : templateId);
      const lang = overrides.language !== undefined ? overrides.language : language;
      const { data } = await api.post(`${apiBase}/${tripId}/render`, {
        provider_id: providerId, reserva_ids: reservaIds, language: lang, template_id: tid,
      });
      setSubject(data.subject || "");
      setBody(data.body || "");
      if (data.language) setLanguage(data.language);
      if (data.to_email && !to) setTo(data.to_email);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Error al generar el borrador"));
    } finally {
      setRendering(false);
    }
  };

  // Borrador inicial al abrir
  useEffect(() => {
    if (open) {
      setTo(toEmail || "");
      setCc("");
      setAttachments([]);
      setTemplateId("auto");
      doRender({ template_id: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onLanguageChange = (v) => { setLanguage(v); doRender({ language: v }); };
  const onTemplateChange = (v) => { setTemplateId(v); doRender({ template_id: v === "auto" ? null : v }); };

  const onTranslate = async () => {
    if (!body) return;
    const targets = LANGS.map((l) => l.code).filter((l) => l !== language);
    const target = window.prompt(`Traducir a (${targets.join(" / ")}):`, targets[0]);
    if (!target || !targets.includes(target)) return;
    setTranslating(true);
    try {
      const { data } = await api.post("/asistente-trip/translate", {
        subject, body, source_lang: language, target_lang: target,
      });
      const nb = (data.body || "").trim();
      if (!nb) { toast.error("La traducción devolvió un mensaje vacío. Vuelve a intentarlo."); return; }
      setSubject((data.subject || "").trim() || subject);
      setBody(nb);
      setLanguage(target);
      toast.success("Traducción aplicada");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Error al traducir"));
    } finally {
      setTranslating(false);
    }
  };

  const onSend = async () => {
    if (!to || !subject || !body) { toast.error("Completa destinatario, asunto y cuerpo"); return; }
    if (!emailConnected) { toast.error("Conecta tu correo antes de enviar"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`${apiBase}/${tripId}/send`, {
        provider_id: providerId, reserva_ids: reservaIds,
        to_email: to, cc: cc || null, subject, body, language,
        attachment_ids: attachments.map((a) => a.file_id),
      });
      const n = data.bookings_marked ?? data.requested ?? reservaIds.length;
      toast.success(`Correo enviado · ${n} servicio(s) actualizado(s)`);
      onClose(true);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Error al enviar"));
    } finally {
      setSending(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose(false)}>
      <SheetContent side="right" className="!max-w-[1100px] w-full sm:!max-w-[1100px] p-0 flex flex-col" data-testid="trip-email-composer">
        <SheetHeader className="px-6 py-4 border-b">
          <SheetTitle className="text-base">
            {multi ? `Correo conjunto · ${reservaIds.length} reservas` : titleSingle}
            {providerName ? ` · ${providerName}` : ""}
          </SheetTitle>
          <SheetDescription className="text-xs">
            {multi
              ? "Un solo correo agrupa todos los servicios seleccionados del mismo proveedor. Al enviarlo, las reservas pasan a Contactado."
              : "Plantilla precargada con los datos del viaje. Revisa antes de enviar."}
          </SheetDescription>
          <div className="text-[11px] mt-1">
            {emailConnected
              ? <span className="text-emerald-700">Se enviará desde <strong>{senderEmail}</strong></span>
              : <span className="text-orange-700">Sin correo conectado · conéctalo desde la cabecera del Asistente.</span>}
          </div>
        </SheetHeader>

        <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 overflow-hidden">
          {/* Editor */}
          <div className="p-6 space-y-4 overflow-y-auto" data-testid="composer-editor">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Idioma</Label>
                <Select value={language} onValueChange={onLanguageChange}>
                  <SelectTrigger data-testid="composer-language-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{LANGS.map((l) => <SelectItem key={l.code} value={l.code}>{l.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Plantilla</Label>
                <Select value={templateId} onValueChange={onTemplateChange}>
                  <SelectTrigger data-testid="composer-template-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Automática</SelectItem>
                    {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} · {(t.language || "").toUpperCase()}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Para</Label>
              <Input value={to} onChange={(e) => setTo(e.target.value)} data-testid="composer-to-input" />
            </div>
            <div className="space-y-1.5">
              <Label>CC (opcional)</Label>
              <Input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="copia@…" data-testid="composer-cc-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Asunto</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="composer-subject-input" />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>Mensaje</Label>
                <div className="flex items-center gap-1.5">
                  <Button type="button" size="sm" variant="ghost" className="h-7 text-xs" onClick={() => doRender()} disabled={rendering} data-testid="composer-regenerate-button">
                    <RefreshCw size={12} className={`mr-1 ${rendering ? "animate-spin" : ""}`} />Regenerar
                  </Button>
                  <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={onTranslate} disabled={translating || !body} data-testid="composer-translate-button">
                    {translating ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Sparkles size={12} className="mr-1" />}Traducir con IA
                  </Button>
                </div>
              </div>
              <Textarea rows={16} value={body} onChange={(e) => setBody(e.target.value)} className="font-mono text-[13px] leading-relaxed mt-1" data-testid="composer-body-textarea" />
              {rendering && <p className="text-[11px] text-muted-foreground mt-1">Generando borrador…</p>}
            </div>
            <div className="space-y-1.5">
              <Label>Adjuntos (opcional)</Label>
              <AttachmentPicker items={attachments} onChange={setAttachments} />
            </div>
          </div>

          {/* Vista previa */}
          <div className="bg-muted/30 border-l p-6 overflow-y-auto hidden lg:block" data-testid="composer-preview">
            <div className="text-[11px] text-muted-foreground uppercase tracking-wider mb-2">Vista previa</div>
            <div className="bg-card border rounded-md p-5">
              <div className="text-xs text-muted-foreground mb-1">Para: <span className="text-foreground">{to || "—"}</span></div>
              {cc && <div className="text-xs text-muted-foreground mb-1">CC: <span className="text-foreground">{cc}</span></div>}
              <div className="text-base font-semibold tracking-tight mb-4">{subject || "—"}</div>
              <pre className="font-sans text-[13px] leading-relaxed whitespace-pre-wrap text-foreground">{body || "—"}</pre>
              {attachments.length > 0 && (
                <div className="mt-3 pt-2 border-t text-[11px] text-muted-foreground">
                  <Paperclip size={11} className="inline mr-1" />{attachments.length} adjunto{attachments.length > 1 ? "s" : ""}: {attachments.map((a) => a.filename).join(", ")}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="border-t px-6 py-3 flex items-center justify-end gap-2 bg-card">
          <Button variant="ghost" onClick={() => onClose(false)} disabled={sending}>Cancelar</Button>
          <Button onClick={onSend} disabled={sending || !emailConnected} data-testid="composer-send-button">
            {sending ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Send size={14} className="mr-2" />}Enviar correo
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
